export const DISALLOWED_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

export const to_safe_object_key = (value) => {
  const normalized = String(value || '').trim();
  return normalized && !DISALLOWED_OBJECT_KEYS.has(normalized) ? normalized : '';
};

export const read_safe_object_value = (record, key, fallback = null) => {
  const safe_key = to_safe_object_key(key);
  if (!safe_key || !record || typeof record !== 'object') return fallback;
  return Object.prototype.hasOwnProperty.call(record, safe_key)
    ? Reflect.get(record, safe_key)
    : fallback;
};

export const normalize_role_name = (role = {}, raw_role = {}) => String(
  raw_role?.role
  || raw_role?.role_name
  || raw_role?.name
  || role?.role
  || role?.role_name
  || role?.name
  || ''
).trim();

export const get_raw_owner_role = (role = {}) => (
  role?.profile_role || (role?.role && typeof role.role === 'object' ? role.role : role)
);

export const get_created_onboarding_status = (detail = {}, creation_response = {}) => [
  detail?.execution_response?.results?.workflow?.initial_status?.status_name,
  detail?.execution_response?.data?.results?.workflow?.initial_status?.status_name,
  detail?.execution_response?.data?.workflow?.initial_status?.status_name,
  detail?.execution_response?.workflow?.initial_status?.status_name,
  creation_response?.execution_response?.results?.workflow?.initial_status?.status_name,
  creation_response?.execution_response?.data?.results?.workflow?.initial_status?.status_name,
  creation_response?.execution_response?.data?.workflow?.initial_status?.status_name,
  creation_response?.execution_response?.workflow?.initial_status?.status_name,
  creation_response?.onboarding_execution?.results?.workflow?.initial_status?.status_name,
  creation_response?.onboarding_execution?.data?.results?.workflow?.initial_status?.status_name,
  creation_response?.onboarding_execution?.workflow?.initial_status?.status_name,
  creation_response?.onboarding_execution?.results?.workflow?.current_status,
  creation_response?.onboarding_execution?.data?.results?.workflow?.current_status,
  creation_response?.onboarding_execution?.workflow?.current_status,
  creation_response?.workflow?.initial_status?.status_name,
  creation_response?.results?.workflow?.initial_status?.status_name,
  creation_response?.data?.workflow?.initial_status?.status_name,
  creation_response?.data?.results?.workflow?.initial_status?.status_name,
  creation_response?.current_workflow_status,
  creation_response?.data?.current_workflow_status,
  creation_response?.application_status,
  creation_response?.data?.application_status,
  detail?.current_workflow_status,
  detail?.application_status
];

export const get_created_onboarding_owner_role_name = (selected_roles = []) => [
  selected_roles[0]?.name,
  selected_roles[0]?.role,
  selected_roles[0]?.role_name
];

export const build_owner_initials = (owner_role_name = '') => owner_role_name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((word) => word.charAt(0).toUpperCase())
  .join('') || '--';

export const build_created_onboarding_template_name = (selected_templates = []) => selected_templates
  .map((template) => template?.name)
  .filter(Boolean)
  .join(', ');

const build_summary_part_candidates = (selected_case = {}) => [
  Array.isArray(selected_case?.onboarding_template_names) ? selected_case.onboarding_template_names.join(', ') : '',
  selected_case?.risk ? `${selected_case.risk} risk` : '',
  selected_case?.eta && selected_case.eta !== '-' ? selected_case.eta : ''
];

export const build_selected_case_header_summary = (selected_case = null) => {
  if (!selected_case) return '';

  const summary_parts = build_summary_part_candidates(selected_case).filter(Boolean);

  return summary_parts.join(' · ') || selected_case.detail || selected_case.stage || '';
};

const build_case_identity = (creation_response, onboarding_form, selected_entity_type, first_defined_value) => ({
  id: first_defined_value([
    creation_response?.application_id,
    creation_response?.data?.application_id,
    onboarding_form?.entity_name
  ], Date.now()),
  application_id: first_defined_value([
    creation_response?.application_id,
    creation_response?.data?.application_id
  ], null),
  entity_profile_id: first_defined_value([
    creation_response?.entity_profile_id,
    creation_response?.data?.entity_profile_id
  ], null),
  entity_type_id: first_defined_value([
    onboarding_form?.entity_type_id,
    selected_entity_type?.id
  ], null)
});

const build_case_display = (onboarding_form, selected_entity_type, onboarding_defaults, normalize_created_onboarding_journey) => {
  const entity_name = String(onboarding_form?.entity_name || '').trim() || 'New onboarding';
  const workflow_status = onboarding_defaults.initial_workflow_status;

  return {
    name: entity_name,
    display_name: entity_name,
    current_workflow_status: workflow_status,
    application_status: workflow_status,
    detail: workflow_status,
    stage: workflow_status,
    journey: normalize_created_onboarding_journey(selected_entity_type),
    journey_key: String(selected_entity_type?.id || selected_entity_type?.journey_key || '').trim().toLowerCase() || 'onboarding',
    journey_tone: String(selected_entity_type?.id || selected_entity_type?.journey_key || '').trim().toLowerCase() || 'onboarding',
    progress: 0,
    progress_tone: 'navy',
    risk: 'Unknown',
    risk_tone: 'unknown',
    activation: workflow_status,
    activation_tone: 'progress'
  };
};

const build_case_assignment = (onboarding_form, selected_project, onboarding_defaults, first_defined_value) => ({
  owner: onboarding_defaults.owner_role_name,
  owner_initials: build_owner_initials(onboarding_defaults.owner_role_name),
  eta: String(onboarding_form?.activate_by || '').trim() || '-',
  onboarding_project_name: first_defined_value([
    selected_project?.name,
    selected_project?.project_name,
    selected_project?.title
  ], '-'),
  onboarding_owner_role_name: onboarding_defaults.owner_role_name,
  onboarding_activate_by: String(onboarding_form?.activate_by || '').trim(),
  onboarding_email: String(onboarding_form?.email || '').trim(),
  template_name: onboarding_defaults.template_name,
  template: onboarding_defaults.template_name
});

export const build_created_onboarding_case_base = ({
  creation_response,
  onboarding_form,
  selected_project,
  selected_entity_type,
  onboarding_defaults = {}
}, first_defined_value, normalize_created_onboarding_journey) => ({
  ...build_case_identity(creation_response, onboarding_form, selected_entity_type, first_defined_value),
  ...build_case_display(onboarding_form, selected_entity_type, onboarding_defaults, normalize_created_onboarding_journey),
  ...build_case_assignment(onboarding_form, selected_project, onboarding_defaults, first_defined_value)
});

export const get_available_roles_from_response = (response) => {
  const candidates = [
    response?.data?.items,
    response?.data?.data,
    response?.data,
    response?.roles,
    response?.items,
    response?.results,
    response
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
};
